﻿import { TrackBasketItem } from '@tk/utilities/tk.basket.action';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

export interface TrackerOptions {
    endpoint?: string;
    basketEventMap?: Record<string, string>;
    favoriteEventMap?: Record<string, string>;
    favoriteActionMap?: Record<string, string>;
}

export interface Tracker {
    init(): Promise<void>;
    push(item: TrackItem): void;
    pushBasket(item: TrackBasket): void;
    pushFavorite(item: TrackFavorite): void;
}

interface TrackerItem {
    name: string;
    instance: Tracker;
}

interface TrackerId {
    name: string;
    key: string;
    endpoint?: string;
}

export interface TrackItem {
    event: string;
    location: string;
    [key: string]: string;
}

export interface TrackBasket {
    event: string;
    location: string;
    currency: string;
    value: string;
    items: TrackBasketItem[];
}

export interface TrackFavorite {
    location: string;
    category: string;
    action: string;
    name: string;
    value: string;
}

export enum TrackerPushTypes {
    BASKET_UPDATE = 'basket-update',
    FAVORITE_UPDATE = 'favorite-update',
}

type TrackerRegistry = {
    [key: string]: (trackingId: string, options?: TrackerOptions) => Promise<Tracker>;
};

export default class TKTracker extends TKCustomElementFactory {
    trackerIds?: TrackerId[];
    trackers: TrackerItem[] = [];
    static registry: TrackerRegistry = {};

    ready: CustomEvent;

    constructor() {
        super();
        this.ready = new CustomEvent('tk-tracker-ready');
    }

    static registerTracker(name: string, factory: (trackingId: string, options?: TrackerOptions) => Promise<Tracker>) {
        this.registry[name] = factory;
    }

    async connectedCallback() {
        this.getTrackingIds();
        if (!this.trackerIds || this.trackerIds.length === 0) throw new Error('Trackier is not defined');

        await this.initTrackers();
        this.registerTrackingListener();
        this.dispatchEvent(this.ready);
    }

    registerTrackingListener() {
        const onTracking = this.handleTracking.bind(this);
        this.pushListener({
            event: 'tk-tracking-push',
            element: this,
            action: onTracking,
        });
    }

    getTrackingIds() {
        try {
            this.trackerIds = JSON.parse(this.getAttribute('data-tk-trackers') || '[]');
        } catch (error) {
            console.error('Error parsing json', error);
        }
    }

    async initTrackers() {
        if (!this.trackerIds) return;
        for (const item of this.trackerIds) {
            try {
                const createTracker = TKTracker.registry[item.name];
                if (createTracker) {
                    const instance = await createTracker(item.key, { endpoint: item.endpoint });
                    await instance.init();
                    this.trackers.push({
                        name: item.name,
                        instance,
                    });
                } else {
                    console.warn(`Tracker "${item.name}" nicht gefunden.`);
                }
            } catch (error) {
                console.error(`This tracker ${item.name} could not be initialized:` + error);
            }
        }
    }

    handleTracking(event: CustomEvent) {
        const { trackerType, type, item } = event.detail;

        this.trackers
            .filter((trackerItem) => !trackerType || trackerItem.name.toLowerCase() === trackerType.toLowerCase())
            .forEach((trackerItem) => {
                if (type === TrackerPushTypes.BASKET_UPDATE) {
                    trackerItem.instance.pushBasket(item);
                } else if (type === TrackerPushTypes.FAVORITE_UPDATE) {
                    trackerItem.instance.pushFavorite(item);
                } else {
                    trackerItem.instance.push(item);
                }
            });
    }
}
